// CREDIT NOTE DRAWER (skeleton 5k): the pure rules behind the ONE drawer and its sheet. No React, no network. Money is exact text, sums in integer cents (BigInt).
// Reuses avoirModel (sourceRows, roomOf) for the events; the server stays authoritative on every cap. Two ways in: FROM EVENTS (a tick and an amount 0..max per event;
// the uncredited remainder stays pending in To do) and FREE CORRECTION (admin only, a reason, an amount capped by the invoice room).
import { cents, text } from '../../../pages/Sales/invoicing/invoicingModel.mjs';
import { sourceRows } from './avoirModel.mjs';

export const WAYS = ['events', 'free'];
export const CAP_CODE = 'CREDIT_NOTE_EXCEEDS_CAP';   // TEMP: the engine still answers AVOIR_EXCEEDS_INVOICE / _RECOGNISED / _SOURCE (renamed in Block 8); the drawer shows this code with every figure
const ZERO_LIKE = /^[0\s.,]*$/;

// a typed amount (already stripped of separators by normalizeAmount, or raw) -> { c: BigInt cents, ok } ; empty and zero are valid (0 = this event stays pending)
export function amountOf(raw, norm) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s || ZERO_LIKE.test(s)) return { c: 0n, ok: true, zero: true };
  return norm ? { c: cents(norm), ok: true, zero: false } : { c: 0n, ok: false, zero: false };
}

// the room of the credit notes on the order: reconcile.room (invoice total - drafts - issued credit notes), the invoice with the most room, the credited-before figure
export function positionOf(data) {
  const rc = (data && data.reconcile) || {};
  const best = (rc.invoices || []).reduce((b, i) => (!b || cents(i.room) > cents(b.room) ? i : b), null);
  const invoiced = rc.kind === 'invoice';
  return { invoiced, invoiceNo: best ? best.invoice_no || '' : '', invoiceId: best ? best.id || null : null, invoiceTotal: invoiced ? rc.invoice_total : rc.recognised, creditedBefore: rc.credit_total || '0.00',
    draftsOnOrder: rc.drafts || '0.00', room: rc.room || '0.00', shouldBe: rc.should_be || '0.00', toCredit: rc.to_credit || '0.00' };
}

// rows of the events table: tick + amount 0..max (max = the event's remaining; a draft line keeps its own amount as max)
export const eventRows = (data) => sourceRows(data).map(r => ({ ...r, max: r.cap }));

// the pre-filled selection: with an amount (the To do row's credit due) it is spread over the open events oldest first, each capped by its max; else every event at its default
export function prefill(rows, amount, onlyRef, draftId) {
  const sel = {};
  // EDIT DRAFT: that draft's own events pre-ticked at their line amounts (editable); every other open event starts unticked
  if (draftId) { for (const r of rows) sel[r.key] = { on: !!r.tickable && r.draft_id === draftId, amount: r.def }; return sel; } let left = amount != null && amount !== '' ? cents(amount) : null;
  const order = [...rows].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  for (const r of order) {
    if (!r.tickable) { sel[r.key] = { on: false, amount: '0' }; continue; }
    if (onlyRef && r.ref !== onlyRef) { sel[r.key] = { on: false, amount: r.def }; continue; }
    if (left == null) { sel[r.key] = { on: true, amount: r.def }; continue; }
    const take = left < cents(r.max) ? left : cents(r.max); left -= take;
    sel[r.key] = { on: take > 0n, amount: take > 0n ? text(take) : r.def };
  }
  return sel;
}

// the state of the form -> lines, per-row errors, the sum, the cap refusal. `norm(raw)` = normalizeAmount with the user's separators.
export function evaluate({ way, rows, sel, free, data, norm }) {
  const pos = positionOf(data); const rowErr = {}; const lines = []; let sum = 0n; let draftTicked = 0n;
  if (way === 'events') {
    for (const r of rows) {
      const s = sel[r.key]; if (!r.tickable || !s || !s.on) continue;
      const a = amountOf(s.amount, norm(s.amount));
      if (!a.ok) { rowErr[r.key] = 'amount'; continue; }
      if (a.c > cents(r.max)) { rowErr[r.key] = 'cap'; continue; }
      if (a.c === 0n) continue;   // ticked at 0 = left pending, no line
      lines.push({ source_kind: r.kind, source_ref: r.ref, amount: text(a.c) }); sum += a.c;
      if (r.draft) draftTicked += cents(r.credited);
    }
  } else {
    const a = amountOf(free.amount, norm(free.amount));
    if (!a.ok || a.zero) rowErr.free = 'amount'; else if (a.c > cents(pos.room)) rowErr.free = 'cap'; else { lines.push({ source_kind: 'other', amount: text(a.c), reason: String(free.reason || '').trim() }); sum += a.c; }
    if (!String(free.reason || '').trim()) rowErr.freeReason = 'reason';
  }
  // the whole note against the invoice room (draft lines already counted in the room are given back)
  const room = cents(pos.room) + draftTicked;
  const over = sum > room;
  const refusal = over ? { code: CAP_CODE, amount: text(sum), room: text(room < 0n ? 0n : room), invoiceTotal: pos.invoiceTotal, creditedBefore: pos.creditedBefore, invoiceNo: pos.invoiceNo } : null;
  const remaining = text(room - sum);
  return { pos, lines, rowErr, sum: text(sum), remaining, refusal };
}

// why the Issue / Save draft buttons are off (i18n key under `cn.why.`), first match; '' = enabled
export function disabledWhy({ loading, noOrder, nothing, ev, way, date, today, busy, otherAllowed }) {
  if (busy) return 'busy';
  if (noOrder) return 'noOrder';
  if (loading) return 'loading';
  if (way === 'free' && !otherAllowed) return 'adminOnly';
  if (nothing && way === 'events') return 'nothingToCredit';
  if (ev.rowErr.freeReason) return 'reasonRequired';
  if (Object.values(ev.rowErr).includes('amount')) return 'amountInvalid';
  if (Object.values(ev.rowErr).includes('cap')) return 'overEvent';
  if (ev.refusal) return 'overCap';
  if (!ev.lines.length) return 'nothingTicked';
  if (date && today && date > today) return 'future';
  return '';
}

// the position of the credit note SHEET as the SERVER serves it (sales-avoirs/:id position: invoice total, credited before, this note, remaining, legacy notes netted); null when the read does not carry it
export function servedPosition(a) {
  const p = a && a.position; if (!p) return null;
  return { invoiceNo: a.invoice_no || '', invoiceTotal: p.invoice_total, creditedBefore: p.credited_before, thisNote: p.this_note, remaining: p.remaining };
}
